import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StoredStockAlert } from '../models/stock-alert.model';

const STORAGE_KEY = 'perron.stock_alerts';

type StockAlertMap = Record<number, StoredStockAlert>;

@Injectable({ providedIn: 'root' })
export class StockAlertStore {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _entries = signal<StockAlertMap>(this.readFromStorage());
  readonly entries = this._entries.asReadonly();

  save(sub: StoredStockAlert): void {
    this._entries.update((prev) => {
      const next = { ...prev, [sub.product_id]: sub };
      this.writeToStorage(next);
      return next;
    });
  }

  getByProductId(productId: number): StoredStockAlert | null {
    return this._entries()[productId] ?? null;
  }

  /**
   * "Este navegador está suscrito al aviso de stock de este producto". Es la
   * única fuente del estado de la campana: la ProductCard y el `notifyActive`
   * que cada superficie pasa al ProductZoomModal leen esto, así que tarjeta y
   * quickview no pueden discrepar. Solo cuenta si hay token, que es lo que
   * necesita `StockAlertModalService.cancel()` para que «Ya no me avises»
   * tenga destino.
   */
  hasAlert(productId: number | string | null | undefined): boolean {
    return productId != null && this.getByProductId(Number(productId)) !== null;
  }

  removeByProductId(productId: number): void {
    this._entries.update((prev) => {
      if (!(productId in prev)) return prev;
      const next = { ...prev };
      delete next[productId];
      this.writeToStorage(next);
      return next;
    });
  }

  removeByToken(token: string): void {
    this._entries.update((prev) => {
      const id = Object.keys(prev).find((key) => prev[Number(key)].token === token);
      if (id == null) return prev;
      const next = { ...prev };
      delete next[Number(id)];
      this.writeToStorage(next);
      return next;
    });
  }

  private readFromStorage(): StockAlertMap {
    if (!this.isBrowser) return {};
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object') return {};
      return parsed as StockAlertMap;
    } catch {
      return {};
    }
  }

  private writeToStorage(map: StockAlertMap): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
      // Quota / private mode — estado en memoria sigue vigente para la sesión.
    }
  }
}
